import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    Alert,
    Spinner
} from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { ArrowBack, PlayArrow, Stop } from '@mui/icons-material';
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "dashjs";
import "videojs-contrib-dash";
import { generateVideoAPI, getSignedUrl } from 'ServerAPI/serverAPI';
import CommonHeader from 'layouts/dashboard/CommonHeader';
import { useToast } from 'context/toast-context/ToastProvider';
import UploadFile from 'components/courses/add-new-course/steps/UploadFile';
import { useResponsive } from 'components/ResponsiveDesign/ResponsiveDesign';
import courseIcon from "../../assets/images/Courses.svg";

const VideoGeneration = () => {
    const [cookies] = useCookies();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showToast } = useToast();
    const { isMobile, isTablet } = useResponsive();
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    // Get videoId from URL params
    const { videoId } = useParams();
    const isViewingMode = !!videoId;

    // Avatar options
    const avatarOptions = [
        { id: 'Onat_Suit_Front_public', name: 'Onat (Professional)', isDefault: true },
        { id: 'SilasHR_public', name: 'Silas (HR)' },
        { id: 'Dexter_Lawyer_Sitting_public', name: 'Dexter (Lawyer)' },
        { id: 'Amina_Blue_Suit_public', name: 'Amina (Blue Suit)' },
        { id: 'Marianne_ProfessionalLook_public', name: 'Marianne (Professional)' },
        { id: 'Alessandra_CasualLook_public', name: 'Alessandra (Casual)' }
    ];

    // State management for generation
    const [formData, setFormData] = useState({
        title: '',
        text_content: '',
        avatar_id: 'Onat_Suit_Front_public'
    });
    const [fileInfo, setFileInfo] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);

    // State management for viewing
    const [videoUrl, setVideoUrl] = useState(null);
    const [videoLoading, setVideoLoading] = useState(false);
    const [videoError, setVideoError] = useState(null);
    const [videoData, setVideoData] = useState(null);

    // Initialize video player when viewing
    useEffect(() => {
        if (isViewingMode && videoUrl && videoRef.current) {
            playerRef.current = videojs(videoRef.current, {
                controls: true,
                responsive: true,
                fluid: true,
                sources: [{
                    src: videoUrl,
                    type: 'application/dash+xml'
                }]
            });

            return () => {
                if (playerRef.current) {
                    playerRef.current.dispose();
                }
            };
        }
    }, [isViewingMode, videoUrl]);

    // Fetch video data when in viewing mode
    useEffect(() => {
        if (isViewingMode && videoId) {
            fetchVideoData();
        }
    }, [videoId, isViewingMode]);

    // Fetch video data and signed URL
    const fetchVideoData = async () => {
        try {
            setVideoLoading(true);
            setVideoError(null);

            const payload = {
                fileName: videoId,
                generatedVideoId: videoId,
                method: "GET",
                userId: cookies?.userId,
            };

            const response = await getSignedUrl(payload);

            if (response.data) {
                setVideoUrl(response.data.preSignedUrl || response.data.objectUrl);
                setVideoData(response.data);
            } else {
                throw new Error('No video URL received');
            }
        } catch (error) {
            console.error('Error fetching video:', error);
            setVideoError(error.message || 'Failed to load video');
            showToast('Failed to load video', 'error');
        } finally {
            setVideoLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setGenerationError(null);
    };

    // Handle file upload
    const handleFileChange = (file) => {
        console.log("file", file);
        setFileInfo(file.target.files[0]);
        setGenerationError(null);
    };

    // Handle avatar selection
    const handleAvatarSelect = (avatarId) => {
        setFormData(prev => ({
            ...prev,
            avatar_id: avatarId
        }));
    };

    // Validate form before submission
    const validateForm = () => {
        if (!formData.title.trim()) {
            setGenerationError('Title is required');
            return false;
        }

        if (!formData.text_content.trim() && !fileInfo) {
            setGenerationError('Either text content or file must be provided');
            return false;
        }

        return true;
    };

    // Handle video generation
    const handleGenerateVideo = async () => {
        if (!validateForm()) return;

        try {
            setIsGenerating(true);
            setGenerationError(null);

            // Prepare form data for API
            const payload = new FormData();
            payload.append('title', formData.title);
            // payload.append('avatar_id', formData.avatar_id);
            payload.append('userId', cookies?.userId);

            if (formData.text_content.trim()) {
                payload.append('text_content', formData.text_content);
            }

            if (fileInfo) {
                payload.append('file', fileInfo);
            }
            console.log("payload", payload);
            // return;
            const response = await generateVideoAPI(payload);

            if (response.data) {
                showToast('Video generation started successfully! Please check back in 5-10 minutes.', 'success');

                // Navigate back to dashboard after successful submission
                setTimeout(() => {
                    navigate('/content-generation-dashboard');
                }, 1000);
            } else {
                showToast('Video generation failed', "Error");
            }
        } catch (error) {
            console.error('Error generating video:', error);
            setGenerationError(error.response?.data?.message || error.message || 'Failed to generate video');
            showToast('Failed to generate video', 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        navigate('/content-generation-dashboard');
    };

    // Render avatar selection cards
    const renderAvatarSelection = () => (
        <div className="mb-4">
            <Form.Label className="fw-semibold mb-3">Select Avatar</Form.Label>
            <div className="row g-3">
                {avatarOptions.map((avatar) => (
                    <div
                        key={avatar.id}
                        className={`col-${isMobile ? '6' : isTablet ? '4' : '2'}`}
                    >
                        <Card
                            className={`h-100 cursor-pointer border-2 ${formData.avatar_id === avatar.id
                                ? 'border-primary bg-info '
                                : 'border-light'
                                }`}
                            onClick={() => handleAvatarSelect(avatar.id)}
                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                            <Card.Body className="p-3 text-center">
                                <div className="mb-2">
                                    <i className="fas fa-user-circle fa-2x "></i>
                                </div>
                                <small className="fw-semibold">
                                    {avatar.name}
                                    {avatar.isDefault && (
                                        <span className="badge bg-success ms-1 small">Default</span>
                                    )}
                                </small>
                            </Card.Body>
                        </Card>
                    </div>
                ))}
            </div>
        </div>
    );

    // Render generation form
    const renderGenerationForm = () => (
        <div>
            {/* Title Input */}
            <div className="mb-4">
                <Form.Label className="fw-semibold">Video Title *</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="e.g., 'Company Overview Presentation' or 'Product Demo Video'"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    disabled={isGenerating}
                    maxLength={100}
                />
                <Form.Text className="text-muted">
                    Enter a descriptive title for your video (max 100 characters)
                </Form.Text>
            </div>

            {/* Avatar Selection */}
            {/* {renderAvatarSelection()} */}

            {/* Content Input Options */}
            <div className="mb-4">
                <Form.Label className="fw-semibold">Content Source *</Form.Label>
                <p className="text-muted small mb-3">
                    Choose either text input or file upload (either one is required)
                </p>

                {/* Text Content */}
                <div className="mb-3">
                    <Form.Label>Text Content</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={6}
                        placeholder="Enter the script or content you want the avatar to speak. For example: 'Welcome to our company! Today I'll be presenting our quarterly results and future roadmap...'"
                        value={formData.text_content}
                        onChange={(e) => handleInputChange('text_content', e.target.value)}
                        disabled={isGenerating}
                        maxLength={5000}
                    />
                    <Form.Text className="text-muted">
                        {formData.text_content.length}/5000 characters
                    </Form.Text>
                </div>

                {/* File Upload */}
                <div className="mb-3">
                    <Form.Label>Or Upload File</Form.Label>
                    <UploadFile
                        fileType="pdfText"
                        handleFileChange={handleFileChange}
                        showFileInfo={true}
                        selectedFile={fileInfo}
                        disabled={isGenerating}
                    />
                    <Form.Text className="text-muted">
                        Upload a PDF or text file containing the content for your video
                    </Form.Text>
                </div>
            </div>

            {/* Error Display */}
            {generationError && (
                <Alert variant="danger" className="mb-4">
                    {generationError}
                </Alert>
            )}

            {/* Generate Button */}
            <div className="d-flex gap-3">
                <Button
                    variant="secondary"
                    onClick={handleBack}
                    disabled={isGenerating}
                >
                    <ArrowBack fontSize="small" className="me-1" />
                    Back
                </Button>
                <Button
                    variant="primary"
                    onClick={handleGenerateVideo}
                    disabled={isGenerating || (!formData.text_content.trim() && !fileInfo)}
                    className="flex-grow-1"
                >
                    {isGenerating ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Generating Video...
                        </>
                    ) : (
                        <>
                            <PlayArrow fontSize="small" className="me-1" />
                            Generate Video
                        </>
                    )}
                </Button>
            </div>

            {/* Generation Progress */}
            {isGenerating && (
                <Alert variant="info" className="mt-4">
                    <div className="d-flex align-items-center">
                        <CircularProgress size={20} className="me-3" />
                        <div>
                            <div className="fw-semibold">Video Generation in Progress</div>
                            <small className="text-muted">
                                Your video is being generated. This typically takes 5-10 minutes.
                                You can safely leave this page and check back later.
                            </small>
                        </div>
                    </div>
                </Alert>
            )}
        </div>
    );

    // Render video viewer
    const renderVideoViewer = () => (
        <div>
            {videoLoading && (
                <div className="text-center p-5">
                    <CircularProgress />
                    <div className="mt-2">Loading video...</div>
                </div>
            )}

            {videoError && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error Loading Video</Alert.Heading>
                    <p className="mb-0">{videoError}</p>
                    <hr />
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="outline-danger" size="sm" onClick={fetchVideoData}>
                            Retry
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handleBack}>
                            Back to Dashboard
                        </Button>
                    </div>
                </Alert>
            )}

            {videoUrl && !videoLoading && !videoError && (
                <div>
                    <div className="mb-3">
                        <h5>Video Preview</h5>
                        {/* <p className="text-muted">Video ID: {videoId}</p> */}
                    </div>

                    <div className="video-container mb-4" style={{ backgroundColor: '#000' }}>
                        <div data-vjs-player>
                            <video
                                ref={videoRef}
                                className="video-js vjs-default-skin"
                                controls
                                preload="auto"
                                data-setup="{}"
                                style={{ width: '100%', height: 'auto' }}
                            />
                        </div>
                    </div>

                    <div className="d-flex gap-3">
                        <Button variant="secondary" onClick={handleBack}>
                            <ArrowBack fontSize="small" className="me-1" />
                            Back to Dashboard
                        </Button>
                        {videoData?.downloadUrl && (
                            <Button
                                variant="outline-primary"
                                href={videoData.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download Video
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="container-fluid">
            <Row>
                <Col>
                    <CommonHeader
                        text={isViewingMode ? "View Video" : "Generate Video"}
                        subText={isViewingMode ? "Watch your generated video" : "Create a new AI-generated video with avatar"}
                        icon={courseIcon}
                    />
                </Col>
            </Row>

            <Row className="mx-auto">
                <Col sm={isViewingMode ? 12 : 10} md={isViewingMode ? 10 : 8} className="mx-auto pt-5 px-0">
                    <Card>
                        <Card.Body className="p-4">
                            {isViewingMode ? renderVideoViewer() : renderGenerationForm()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default VideoGeneration;